import type {
  Product,
  Revision,
  BOMItem,
  Phase,
  ExplodedItem,
  CostRollup,
  WhereUsedEntry,
  ECN,
  Build,
  BuildIssue,
  ProductPhaseInfo,
} from '../types/bom';

const API_BASE = '/api/v1/bom';
const DIGIKEY_BASE = '/api/v1/digikey';

// Helper function for authenticated requests
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response;
}

// ============================================================================
// Product API
// ============================================================================

export async function listProducts(phase?: Phase): Promise<Product[]> {
  const url = phase ? `${API_BASE}/products?phase=${phase}` : `${API_BASE}/products`;
  const response = await fetchWithAuth(url);
  return response.json();
}

export async function getProduct(productId: string): Promise<Product> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}`);
  return response.json();
}

export async function createProduct(product: Partial<Product>): Promise<Product> {
  const response = await fetchWithAuth(`${API_BASE}/products`, {
    method: 'POST',
    body: JSON.stringify(product),
  });
  return response.json();
}

export async function updateProduct(productId: string, product: Partial<Product>): Promise<Product> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(product),
  });
  return response.json();
}

// ============================================================================
// Revision API
// ============================================================================

export async function listRevisions(productId: string): Promise<Revision[]> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/revisions`);
  return response.json();
}

export async function getRevision(productId: string, revision: string): Promise<Revision> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/revisions/${revision}`);
  return response.json();
}

export async function createRevision(productId: string, revision: Partial<Revision>): Promise<Revision> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/revisions`, {
    method: 'POST',
    body: JSON.stringify(revision),
  });
  return response.json();
}

export async function updateRevision(
  productId: string,
  revisionId: string,
  revision: Partial<Revision>
): Promise<Revision> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/revisions/${revisionId}`, {
    method: 'PUT',
    body: JSON.stringify(revision),
  });
  return response.json();
}

// ============================================================================
// BOM Operations API
// ============================================================================

export async function explodeBOM(
  productId: string,
  revision: string,
  qty: number = 1
): Promise<ExplodedItem[]> {
  const response = await fetchWithAuth(
    `${API_BASE}/products/${productId}/revisions/${revision}/explode?qty=${qty}`
  );
  return response.json();
}

export async function getCostRollup(productId: string, revision: string): Promise<CostRollup> {
  const response = await fetchWithAuth(
    `${API_BASE}/products/${productId}/revisions/${revision}/cost`
  );
  return response.json();
}

export async function getTBDParts(productId: string): Promise<BOMItem[]> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/tbd`);
  return response.json();
}

export async function resolveTBDPart(
  productId: string,
  revision: string,
  lineNo: number,
  partCode: string
): Promise<void> {
  await fetchWithAuth(`${API_BASE}/products/${productId}/revisions/${revision}/tbd/${lineNo}`, {
    method: 'PUT',
    body: JSON.stringify({ part_code: partCode }),
  });
}

export async function getWhereUsed(partCode: string): Promise<WhereUsedEntry[]> {
  const response = await fetchWithAuth(`${API_BASE}/whereused/${encodeURIComponent(partCode)}`);
  return response.json();
}

// ============================================================================
// Phase Management API
// ============================================================================

export async function getPhaseSummary(): Promise<Record<Phase, number>> {
  const response = await fetchWithAuth(`${API_BASE}/phases/summary`);
  return response.json();
}

export async function getPhaseProducts(phase: Phase): Promise<ProductPhaseInfo[]> {
  const response = await fetchWithAuth(`${API_BASE}/phases/${phase}/products`);
  return response.json();
}

export async function transitionPhase(
  productId: string,
  newPhase: Phase,
  checklist: Record<string, boolean>,
  notes?: string
): Promise<void> {
  await fetchWithAuth(`${API_BASE}/products/${productId}/transition`, {
    method: 'POST',
    body: JSON.stringify({
      new_phase: newPhase,
      checklist,
      notes,
    }),
  });
}

// ============================================================================
// ECN API
// ============================================================================

export async function listECNs(productId: string): Promise<ECN[]> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/ecns`);
  return response.json();
}

export async function createECN(productId: string, ecn: Partial<ECN>): Promise<ECN> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/ecns`, {
    method: 'POST',
    body: JSON.stringify(ecn),
  });
  return response.json();
}

export async function getECN(ecnId: string): Promise<ECN> {
  const response = await fetchWithAuth(`${API_BASE}/ecns/${ecnId}`);
  return response.json();
}

export async function approveECN(ecnId: string): Promise<void> {
  await fetchWithAuth(`${API_BASE}/ecns/${ecnId}/approve`, {
    method: 'POST',
  });
}

// ============================================================================
// Build API
// ============================================================================

export async function listBuilds(productId: string): Promise<Build[]> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/builds`);
  return response.json();
}

export async function createBuild(productId: string, build: Partial<Build>): Promise<Build> {
  const response = await fetchWithAuth(`${API_BASE}/products/${productId}/builds`, {
    method: 'POST',
    body: JSON.stringify(build),
  });
  return response.json();
}

export async function getBuild(buildId: string): Promise<Build> {
  const response = await fetchWithAuth(`${API_BASE}/builds/${buildId}`);
  return response.json();
}

export async function updateBuildStatus(buildId: string, status: string): Promise<void> {
  await fetchWithAuth(`${API_BASE}/builds/${buildId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function addBuildIssue(buildId: string, issue: Partial<BuildIssue>): Promise<void> {
  await fetchWithAuth(`${API_BASE}/builds/${buildId}/issues`, {
    method: 'POST',
    body: JSON.stringify(issue),
  });
}

// ============================================================================
// Part Codes API (for BOM Add Part dropdown)
// ============================================================================

/**
 * Part Type from 電子零件別 sheet
 * Used to categorize electronic parts (R for resistor, C for capacitor, etc.)
 */
export interface PartTypeOption {
  part_type_en: string;  // English name (e.g., "Resistor")
  part_type_zh: string;  // Chinese name (e.g., "電阻")
  part_code: string;     // Type code (e.g., "R")
  label: string;         // Display format: "{part_code}-{part_type_zh}"
}

/**
 * Category info for part sheets
 * Each category corresponds to a sheet in the spreadsheet
 */
export interface PartCategory {
  id: string;            // Category ID (e.g., "electronic_parts")
  name: string;          // Chinese name (e.g., "電子零件")
  sheet_name: string;    // Sheet name in spreadsheet
}

/**
 * Part code record from category sheets
 *
 * Sheet Structure (Spreadsheet ID: 1NcDtQziOsIsoX7EP_tgoBDvm4aZfoONzGFCT9XZjhnU):
 * Available category sheets:
 *   - 電子零件 (electronic_parts) - Electronic components
 *   - 電子模組 (electronic_modules) - Electronic modules
 *   - 塑膠零件 (plastic_parts) - Plastic parts
 *   - 金屬零件 (metal_parts) - Metallic parts
 *   - 光學零件 (optical_parts) - Optical components
 *   - 螺絲 (screws) - Screws
 *   - 耗材 (consumables) - Consumables
 */
export interface PartCodeRecord {
  row_index: number;
  part_number: string;    // A - 雲騰品號 (Internal part number)
  vendor_name: string;    // B - 廠商名稱 (Vendor name)
  brand: string;          // C - 廠牌 (Brand)
  vendor_product: string; // D - 廠商品名 (Vendor product name)
  vendor_spec: string;    // E - 廠商規格 (Vendor specification)
  category: string;       // F - 類別 (Part type code, e.g., "R" for resistor)
  mpq: string;            // G - MPQ (Minimum pack quantity)
}

/**
 * Get part types from 電子零件別 sheet
 * Used for filtering electronic parts by type (R, C, IC, etc.)
 */
export async function getPartTypes(): Promise<PartTypeOption[]> {
  const response = await fetchWithAuth('/api/v1/partcodes/parttypes');
  return response.json();
}

/**
 * Get all available part categories
 */
export async function getPartCategories(): Promise<PartCategory[]> {
  const response = await fetchWithAuth('/api/v1/partcodes/categories');
  return response.json();
}

/**
 * Get parts from a category sheet, optionally filtered by part type code
 *
 * @param categoryId - Category ID (e.g., "electronic_parts")
 * @param typeCode - Optional part type code (e.g., "R" for resistors)
 *                   Only applicable for electronic_parts category
 */
export async function getPartCodes(categoryId: string, typeCode?: string): Promise<PartCodeRecord[]> {
  const url = typeCode
    ? `/api/v1/partcodes/${categoryId}?type=${encodeURIComponent(typeCode)}`
    : `/api/v1/partcodes/${categoryId}`;
  const response = await fetchWithAuth(url);
  return response.json();
}

// ============================================================================
// Batch Part Code Operations
// ============================================================================

/**
 * Request body for batch creating part codes
 */
export interface BatchCreatePartCodesRequest {
  part_codes: {
    part_number: string;      // A - 雲騰品號 (Internal part number)
    vendor_name: string;      // B - 廠商名稱 (Vendor name)
    brand: string;            // C - 廠牌 (Brand)
    vendor_product: string;   // D - 廠商品名 (Vendor product name)
    vendor_spec: string;      // E - 廠商規格 (Vendor specification)
    category: string;         // F - 類別 (Part type code)
    dimension?: string;       // G - 尺寸 (Size/dimension)
    mpq?: string;             // P - MPQ (Minimum pack quantity)
  }[];
}

/**
 * Response from batch creating part codes
 */
export interface BatchCreatePartCodesResponse {
  success_count: number;
  total_count: number;
  errors?: string[];
}

/**
 * Batch create part codes in a category
 * @param categoryId - Category ID (e.g., "electronic_parts")
 * @param partCodes - Array of part codes to create
 */
export async function batchCreatePartCodes(
  categoryId: string,
  partCodes: BatchCreatePartCodesRequest['part_codes']
): Promise<BatchCreatePartCodesResponse> {
  const response = await fetchWithAuth(`/api/v1/partcodes/${categoryId}/batch`, {
    method: 'POST',
    body: JSON.stringify({ part_codes: partCodes }),
  });
  return response.json();
}

// ============================================================================
// DigiKey MLCC Search API
// ============================================================================

/**
 * Single MLCC search result from DigiKey
 */
export interface DigiKeyMLCCResult {
  input: string;
  digikey_part_number: string;
  manufacturer_part_number: string;
  manufacturer_name: string;
  description: string;
  unit_price: number;
  quantity_available: number;
  product_url: string;
  package_size?: string;
  capacitance?: string;
  voltage?: string;
  dielectric?: string;
}

/**
 * Group of search results for a single input spec
 */
export interface DigiKeyMLCCResultGroup {
  input: string;
  products: DigiKeyMLCCResult[];
  total_count: number;
  error?: string;
}

/**
 * Response from MLCC search
 */
export interface DigiKeyMLCCSearchResponse {
  results: DigiKeyMLCCResultGroup[];
  errors?: string[];
}

/**
 * DigiKey authentication status
 */
export interface DigiKeyAuthStatus {
  authenticated: boolean;
  expires_at?: string;
  updated_by?: string;
}

/**
 * Search DigiKey for MLCC parts by specification
 * @param specs - Array of MLCC specifications (e.g., ["10uF/50V/X5R/1206"])
 * @deprecated Use searchDigiKeyParts instead for generic part type support
 */
export async function searchDigiKeyMLCC(specs: string[]): Promise<DigiKeyMLCCSearchResponse> {
  const response = await fetchWithAuth('/api/v1/digikey/mlcc/search', {
    method: 'POST',
    body: JSON.stringify({ specs }),
  });
  return response.json();
}

/**
 * Search DigiKey for parts by part type and specification
 * Uses the DigiKey category mapping from 電子零件別 sheet
 * @param partCode - Part type code from 電子零件別 (e.g., "C", "R", "STC", "IC")
 * @param specs - Array of specifications to search for
 * @param useAIParse - Whether to use AI for parsing free-form text (optional)
 */
export async function searchDigiKeyParts(
  partCode: string,
  specs: string[],
  useAIParse: boolean = false
): Promise<DigiKeyMLCCSearchResponse> {
  const response = await fetchWithAuth('/api/v1/digikey/parts/search', {
    method: 'POST',
    body: JSON.stringify({ part_code: partCode, specs, use_ai_parse: useAIParse }),
  });
  return response.json();
}

/**
 * Get DigiKey authentication status
 */
export async function getDigiKeyAuthStatus(): Promise<DigiKeyAuthStatus> {
  const response = await fetchWithAuth('/api/v1/digikey/auth/status');
  return response.json();
}

// ============================================================================
// DigiKey Part Type API
// ============================================================================

/**
 * Part type with DigiKey-specific metadata
 * Combines data from 電子零件別 sheet with DigiKey category mappings
 */
export interface DigiKeyPartType {
  part_code: string;           // Type code from sheet (e.g., "C", "R", "IC")
  part_type_en: string;        // English name (e.g., "Capacitor")
  part_type_zh: string;        // Chinese name (e.g., "電容")
  label: string;               // Display label: "{part_code}-{part_type_zh}"
  digikey_category?: string;   // DigiKey category for filtering
  format?: string;             // Expected input format hint
  placeholder?: string;        // Example input for placeholder
  attributes?: string[];       // Parseable attributes for this type
}

/**
 * Get available part types for DigiKey search
 * Returns part types from 電子零件別 sheet with DigiKey-specific metadata
 */
export async function getDigiKeyPartTypes(): Promise<DigiKeyPartType[]> {
  const response = await fetchWithAuth('/api/v1/digikey/part-types');
  return response.json();
}

// ============================================================================
// DigiKey AI Parsing API
// ============================================================================

/**
 * AI status response
 */
export interface AIStatusResponse {
  ai_enabled: boolean;
  ai_available: boolean;
}

/**
 * Get AI parsing status
 */
export async function getDigiKeyAIStatus(): Promise<AIStatusResponse> {
  const response = await fetchWithAuth('/api/v1/digikey/ai/status');
  return response.json();
}

/**
 * Parse request for DigiKey part specifications
 */
export interface DigiKeyParseRequest {
  part_code: string;     // Part type code (e.g., "C", "R", "IC")
  texts: string[];       // Free-form texts to parse
  use_ai_parse?: boolean; // Whether to use AI for unstructured text
  dry_run?: boolean;     // Only parse, don't search
}

/**
 * Parse result for a single specification
 */
export interface DigiKeyParseResult {
  original: string;
  parsed?: Record<string, string>;
  search_query: string;
  method: 'structured' | 'ai' | 'raw';
  confidence: number;
  notes?: string;
  error?: string;
}

/**
 * Parse response from DigiKey
 */
export interface DigiKeyParseResponse {
  results: DigiKeyParseResult[];
  ai_enabled: boolean;
}

/**
 * Parse free-form part specifications using structured parsing and/or AI
 */
export async function parseDigiKeySpecs(request: DigiKeyParseRequest): Promise<DigiKeyParseResponse> {
  const response = await fetchWithAuth('/api/v1/digikey/parse', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  return response.json();
}

// ============================================================================
// BOM Import API
// ============================================================================

/**
 * Column mapping for BOM file import
 */
export interface BOMColumnMapping {
  part_code: number;    // Column index for part code/number
  description: number;  // Column index for description
  quantity: number;     // Column index for quantity
  unit: number;         // Column index for unit (pcs, ea, etc.)
  ref_des: number;      // Column index for reference designator
  level: number;        // Column index for BOM level (optional)
  confidence: number;   // Confidence score of detection
}

/**
 * Parsed spec from AI
 */
export interface ParsedSpec {
  part_code: string;
  original_text: string;
  attributes?: Record<string, string>;
  search_query: string;
  confidence: number;
  method: 'structured' | 'ai' | 'raw';
  error?: string;
}

/**
 * DigiKey result for import preview
 */
export interface BOMDigiKeyResult {
  mpn: string;
  manufacturer: string;
  description: string;
  unit_price: number;
  stock: number;
  digikey_pn: string;
}

/**
 * Single item in BOM import preview
 */
export interface BOMImportItem {
  line_no: number;
  raw_data: Record<string, string>;
  part_code: string;
  description: string;
  qty_per: number;
  unit: string;
  ref_des: string;
  level: number;
  parsed_spec?: ParsedSpec;
  digikey_result?: BOMDigiKeyResult;
  status: 'confirmed' | 'tbd' | 'error';
  error?: string;
}

/**
 * Parse error from BOM file
 */
export interface BOMParseError {
  line_no: number;
  column?: string;
  message: string;
}

/**
 * BOM import preview data
 */
export interface BOMImportPreview {
  file_name: string;
  file_type: 'csv' | 'xlsx' | 'xls';
  total_rows: number;
  parsed_rows: number;
  error_rows: number;
  column_mapping: BOMColumnMapping;
  detected_headers: string[];
  items: BOMImportItem[];
  errors: BOMParseError[];
}

/**
 * BOM import preview response
 */
export interface BOMImportPreviewResponse {
  success: boolean;
  preview?: BOMImportPreview;
  error?: string;
}

/**
 * BOM import result
 */
export interface BOMImportResult {
  success: boolean;
  product_id: string;
  revision: string;
  items_imported: number;
  items_as_tbd: number;
  items_failed: number;
  ai_parse_count: number;
  digikey_search_count: number;
  estimated_ai_cost_usd: number;
  error?: string;
}

/**
 * Options for BOM import
 */
export interface BOMImportOptions {
  useAIParse?: boolean;
  searchDigiKey?: boolean;
  columnMapping?: Partial<BOMColumnMapping>;
  sheetName?: string;
}

/**
 * Preview a BOM file import without saving
 * @param file - CSV or Excel file to import
 * @param options - Import options
 */
export async function previewBOMImport(
  file: File,
  options: BOMImportOptions = {}
): Promise<BOMImportPreviewResponse> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);

  if (options.useAIParse) {
    formData.append('use_ai_parse', 'true');
  }
  if (options.searchDigiKey) {
    formData.append('search_digikey', 'true');
  }
  if (options.columnMapping) {
    formData.append('column_mapping', JSON.stringify(options.columnMapping));
  }
  if (options.sheetName) {
    formData.append('sheet_name', options.sheetName);
  }

  const response = await fetch(`${API_BASE}/import/preview`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Import a BOM file into an existing revision
 * @param productId - Product ID
 * @param revision - Revision code
 * @param file - CSV or Excel file to import
 * @param options - Import options
 */
export async function importBOMToRevision(
  productId: string,
  revision: string,
  file: File,
  options: BOMImportOptions = {}
): Promise<BOMImportResult> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);

  if (options.useAIParse) {
    formData.append('use_ai_parse', 'true');
  }
  if (options.searchDigiKey) {
    formData.append('search_digikey', 'true');
  }
  if (options.columnMapping) {
    formData.append('column_mapping', JSON.stringify(options.columnMapping));
  }
  if (options.sheetName) {
    formData.append('sheet_name', options.sheetName);
  }

  const response = await fetch(`${API_BASE}/products/${productId}/revisions/${revision}/import`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Import a BOM file as a new revision
 * @param productId - Product ID
 * @param newRevision - New revision code (e.g., "B", "C")
 * @param file - CSV or Excel file to import
 * @param options - Import options
 */
export async function importBOMAsNewRevision(
  productId: string,
  newRevision: string,
  file: File,
  options: BOMImportOptions = {}
): Promise<BOMImportResult> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('new_revision', newRevision);

  if (options.useAIParse) {
    formData.append('use_ai_parse', 'true');
  }
  if (options.searchDigiKey) {
    formData.append('search_digikey', 'true');
  }
  if (options.columnMapping) {
    formData.append('column_mapping', JSON.stringify(options.columnMapping));
  }
  if (options.sheetName) {
    formData.append('sheet_name', options.sheetName);
  }

  const response = await fetch(`${API_BASE}/products/${productId}/import`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// BOM Import Workflow API (New Multi-Step Flow)
// ============================================================================

/**
 * Internal part match from Google Sheets 電子零件 database
 */
export interface InternalPartMatch {
  part_number: string;     // 雲騰品號
  vendor_name: string;     // 廠商名稱
  brand: string;           // 廠牌
  vendor_product: string;  // 廠商品名
  vendor_spec: string;     // 廠商規格
  category: string;        // 類別 (R, C, IC, etc.)
  dimension: string;       // 尺寸
  match_score: number;     // 0-1 match score
  match_reason: string;    // Why it matched
}

/**
 * DigiKey part match
 */
export interface DigiKeyPartMatch {
  digikey_pn: string;
  mpn: string;
  manufacturer: string;
  description: string;
  unit_price: number;
  stock: number;
  min_order_qty: number;
  package_type: string;
  product_url?: string;
}

/**
 * Single item in the BOM import workflow
 */
export interface BOMWorkflowItem {
  line_no: number;
  raw_data: Record<string, string>;
  part_type: string;           // From CSV Part Type column (SMC, SMR, etc.)
  description: string;
  qty_per: number;
  unit: string;
  ref_des: string;
  status: 'pending' | 'found' | 'matched' | 'digikey' | 'manual' | 'error';
  error?: string;

  // Internal search results
  internal_matches?: InternalPartMatch[];
  selected_part_no?: string;

  // DigiKey search results
  digikey_matches?: DigiKeyPartMatch[];
  selected_digikey?: DigiKeyPartMatch;
}

/**
 * Response from parsing a BOM CSV file
 */
export interface BOMParseResponse {
  success: boolean;
  file_name: string;
  file_type: string;
  total_rows: number;
  parsed_rows: number;
  error_rows: number;
  column_mapping: BOMColumnMapping;
  detected_headers: string[];
  items: BOMWorkflowItem[];
  errors: BOMParseError[] | null;
  error?: string;
}

/**
 * Response from searching internal parts database
 */
export interface SearchInternalResponse {
  success: boolean;
  items: BOMWorkflowItem[];
  matched_count: number;
  pending_count: number;
  error?: string;
}

/**
 * Response from searching DigiKey
 */
export interface SearchDigiKeyResponse {
  success: boolean;
  items: BOMWorkflowItem[];
  matched_count: number;
  pending_count: number;
  error?: string;
  // Auth fields - when DigiKey authentication is required
  auth_required?: boolean;  // True if DigiKey OAuth is needed
  auth_url?: string;        // OAuth URL to redirect user to
}

/**
 * Request to confirm and save BOM
 */
export interface ConfirmBOMRequest {
  product_id: string;
  revision?: string;          // Existing revision
  new_revision?: string;      // Code for new revision (e.g., "B")
  items: BOMWorkflowItem[];
  create_new_parts?: boolean; // Whether to create new part codes
}

/**
 * Response from confirming BOM
 */
export interface ConfirmBOMResponse {
  success: boolean;
  product_id: string;
  revision: string;
  items_imported: number;
  new_parts_created: number;
  error?: string;
}

/**
 * Parse a BOM CSV file without searching
 * Step 1 of the import workflow
 */
export async function parseBOMFile(file: File, sheetName?: string): Promise<BOMParseResponse> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);

  if (sheetName) {
    formData.append('sheet_name', sheetName);
  }

  const response = await fetch(`${API_BASE}/import/parse`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Search internal parts database for matches
 * Step 2 of the import workflow
 */
export async function searchInternalParts(items: BOMWorkflowItem[]): Promise<SearchInternalResponse> {
  const response = await fetchWithAuth(`${API_BASE}/import/search-internal`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
  return response.json();
}

/**
 * Search DigiKey for unmatched parts
 * Step 3 of the import workflow (optional)
 */
export async function searchDigiKeyBatch(items: BOMWorkflowItem[]): Promise<SearchDigiKeyResponse> {
  const response = await fetchWithAuth(`${API_BASE}/import/search-digikey`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
  return response.json();
}

/**
 * Confirm and save BOM to database
 * Final step of the import workflow
 */
export async function confirmBOMImport(request: ConfirmBOMRequest): Promise<ConfirmBOMResponse> {
  const response = await fetchWithAuth(`${API_BASE}/import/confirm`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

// ========== AI Web Search Types and Functions ==========

/**
 * AI-suggested part from web search
 */
export interface WebSearchPart {
  manufacturer_pn: string;
  manufacturer: string;
  description: string;
  specifications: Record<string, string>;
  estimated_price?: string;
  source_url?: string;
  confidence: number;
  alternatives?: string[];
}

/**
 * Web search result from AI
 */
export interface WebSearchResult {
  query: string;
  parts: WebSearchPart[];
  sources: string[];
  search_method: 'ai_knowledge' | 'web_search';
  confidence: number;
  ai_explanation: string;
  prompt?: string; // The exact prompt sent to Claude AI
}

/**
 * Web search response including rate limit info
 */
export interface WebSearchResponse {
  result: WebSearchResult;
  rate_limit?: {
    remaining: number;
    reset_at: string;
  };
}

/**
 * Search for parts online using AI when DigiKey returns no results
 * @param partCode - Part type code (e.g., "C", "R", "IC")
 * @param spec - User's original specification
 * @param originalQuery - The DigiKey query that returned no results
 * @param digikeyError - Error message from DigiKey
 * @param model - Optional: Claude model ID to use (overrides server default)
 */
export async function searchPartsOnline(
  partCode: string,
  spec: string,
  originalQuery: string,
  digikeyError: string,
  model?: string
): Promise<WebSearchResponse> {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${DIGIKEY_BASE}/parts/web-search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      part_code: partCode,
      spec,
      original_query: originalQuery,
      digikey_error: digikeyError,
      ...(model ? { model } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ==============================================================================
// Netlist Import Types and Functions
// ==============================================================================

/**
 * Response from parsing a KiCad netlist file
 */
export interface NetlistParseResponse {
  success: boolean;
  file_name: string;
  source_file?: string;
  tool?: string;
  total_components: number;
  unique_items: number;
  items: BOMWorkflowItem[];
  summary: Record<string, number>;
  errors?: BOMParseError[];
  error?: string;
}

/**
 * Request for project-BOM operations
 */
export interface ProjectBOMRequest {
  action: 'create' | 'link';
  product_id?: string;
  product_name?: string;
  product_code?: string;
  category?: string;
}

/**
 * Linked product summary (subset of Product fields)
 */
export interface LinkedProduct {
  product_id: string;
  product_code: string;
  product_name: string;
  category: string;
  current_phase: string;
  current_revision: string;
  status: string;
}

/**
 * Response from project-BOM operations
 */
export interface ProjectBOMResponse {
  success: boolean;
  product_id?: string;
  product?: LinkedProduct;
  message?: string;
  error?: string;
}

/**
 * Parse a KiCad netlist file
 * @param file Markdown netlist file
 * @param aggregate Whether to aggregate duplicate components (default: true)
 */
export async function parseNetlistFile(file: File, aggregate: boolean = true): Promise<NetlistParseResponse> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('aggregate', String(aggregate));

  const response = await fetch(`${API_BASE}/import/parse-netlist`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Link or create a BOM product for a project
 * @param projectId Project ID
 * @param request Action to perform (create or link)
 */
export async function linkProjectBOM(projectId: string, request: ProjectBOMRequest): Promise<ProjectBOMResponse> {
  const token = localStorage.getItem('auth_token');

  const response = await fetch(`/api/v1/projects/${projectId}/bom`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Get the BOM product linked to a project
 * @param projectId Project ID
 */
export async function getProjectBOM(projectId: string): Promise<ProjectBOMResponse> {
  const token = localStorage.getItem('auth_token');

  const response = await fetch(`/api/v1/projects/${projectId}/bom`, {
    method: 'GET',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}
